import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'

export type RsvpWireStatus = 'accepted' | 'rejected'

/**
 * Hunt lifecycle as the callable reports it — its `huntLifecycleState`.
 *
 * Only these three. A PAUSED hunt reports as 'ongoing': the function looks for
 * ongoing and paused together and labels both that way. 'notStarted' means it
 * found no ongoing, paused or ended hunt for this plan — the plan is still
 * upcoming and no hunt document was touched. Anything else, including an
 * omitted value, normalizes to null rather than reaching the UI.
 */
export type HuntWireState = 'notStarted' | 'ongoing' | 'ended'

/**
 * Envelope the `respondtohuntplaninvite` callable resolves with.
 *
 * Read defensively: the function reports its own failures as a resolved
 * `status: "ERROR"` envelope rather than by throwing — invite-not-found,
 * plan-gone, a rejected request body, and an unhandled server error all arrive
 * this way — so only an explicit SUCCESS counts as success.
 */
export interface RespondInviteEnvelope {
  status?: string
  message?: string
  isHuntOngoing?: boolean
  huntId?: string | null
  huntState?: string | null
}

/**
 * What the caller gets back on success — the callable's enrichment, normalized.
 *
 * `huntId` is the id of the HUNT (the `hunts` collection), not of the hunt plan
 * the invitation belongs to — two different documents with two different ids,
 * so it can never be substituted with the plan id the caller already has. The
 * callable hands it back because it has just resolved it; deriving it here
 * would mean the web app running its own `hunts` query.
 */
export interface RespondInviteResult {
  isHuntOngoing: boolean
  huntId: string | null
  huntState: HuntWireState | null
}

const HUNT_STATES: readonly string[] = ['notStarted', 'ongoing', 'ended']

/** Unknown/absent states normalize to null rather than leaking a raw value. */
export function normalizeHuntState(raw: string | null | undefined): HuntWireState | null {
  return typeof raw === 'string' && HUNT_STATES.includes(raw) ? (raw as HuntWireState) : null
}

export function isRespondSuccess(data: RespondInviteEnvelope | undefined | null): boolean {
  return data?.status?.trim().toUpperCase() === 'SUCCESS'
}

/**
 * The hunt whose ended-report this response should open, or null to stay put.
 *
 * Accepting an invitation to a hunt that has ALREADY ENDED has nowhere else to
 * go: there is nothing left to attend, and the confirmed-RSVP footer would be a
 * dead end. The callable leaves an ended hunt's rosters alone and adds the user
 * to its `userIds` only, so the hunt appears in their ended list afterwards.
 *
 * Guarded on accept. Declining an ended hunt must not open its report — the
 * user just said they were not there.
 */
export function endedHuntToOpen(
  status: RsvpWireStatus,
  result: RespondInviteResult,
): string | null {
  if (status !== 'accepted') return null
  if (result.huntState !== 'ended') return null
  return result.huntId
}

/**
 * Turns the signed-in user's position publishing on after they join a hunt
 * that is already running.
 *
 * The callable never touches `menuConfiguration` — no cloud function does — so
 * each client does it for itself, and this mirrors what the mobile app does
 * once its response commits. The setting lives on the user document rather
 * than the device, which is the whole reason a web RSVP has to write it: the
 * hunter who accepts at a desk is the same one who later hunts with a phone,
 * and joining a live hunt is taken to imply being visible in it.
 *
 * Deliberately total, and never awaited by the caller: the response has
 * already committed by the time this runs, so a slow or failing settings write
 * must not delay it or turn a successful RSVP into an error.
 */
async function enablePositionPublishing(uid: string): Promise<void> {
  try {
    const [{ getApp }, { getFirestore, doc, updateDoc }] = await Promise.all([
      import('firebase/app'),
      import('firebase/firestore'),
    ])
    // Dotted path, not a nested object: a nested map would replace the entire
    // menuConfiguration and wipe every other map setting the user has.
    await updateDoc(doc(getFirestore(getApp()), 'users', uid), {
      'menuConfiguration.publishMyPosition': true,
    })
  } catch (e) {
    console.error('Failed to enable position publishing after joining a live hunt', e)
  }
}

/**
 * Respond to a hunt invitation via the `respondtohuntplaninvite` Firebase
 * callable (europe-west1) — the same function the mobile app uses.
 *
 * This deliberately does NOT go through the .NET API, unlike most of clubhouse
 * web. That endpoint only ever mirrored the first half of this function: it
 * records the answer, but never adds an accepting hunter to a hunt that is
 * already running, so accepting a live hunt from web left them out of its
 * participants entirely — invisible on the live map, absent from hunt chat and
 * PTT, unassignable to a stand.
 *
 * Not for want of information: the plan resolves to its hunt through a `hunts`
 * query on `huntPlan.id`, and the composite index for it exists, so .NET could
 * do that much. It is the rest that is not worth owning twice. Adding the user
 * to a running hunt is a transaction reconciling three fields
 * (`huntPlan.participants`, `userIds`, `hunters`) that dedupes an existing
 * entry and clears its `leaveDate` rather than appending a second. A late
 * accept of an ended hunt writes `userIds` alone, so the user can find it again
 * without being recorded as having hunted it. And reject stays idempotent
 * against a plan or invite deleted mid-flight. A second implementation of that
 * is a second thing to keep in step, for no behaviour that is not already here.
 *
 * What the function does NOT do is touch `menuConfiguration` — position
 * publishing is each client's own job, handled above.
 *
 * Mirrors `useJoinHuntByCode`'s direct-callable pattern, which reaches
 * `joinWithCode` in this same function family for the same reason.
 */
export function useRespondToHuntInvite(groupId: string | undefined, huntId: string | undefined) {
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      status,
      responseText,
    }: {
      status: RsvpWireStatus
      responseText?: string
    }): Promise<RespondInviteResult> => {
      if (!huntId) throw new Error('Missing hunt id for RSVP')
      const [{ getApp }, { getFunctions, httpsCallable }] = await Promise.all([
        import('firebase/app'),
        import('firebase/functions'),
      ])
      const fns = getFunctions(getApp(), 'europe-west1')
      const call = httpsCallable<
        { huntPlanId: string; status: RsvpWireStatus; responseText: string },
        RespondInviteEnvelope
      >(fns, 'respondtohuntplaninvite')
      // '' rather than null for an omitted note. The callable accepts either,
      // but stores what it is given, so the choice decides what ends up on the
      // plan's response. Mobile sends '', and one shape from both clients beats
      // two for whoever reads these back.
      const res = await call({ huntPlanId: huntId, status, responseText: responseText ?? '' })
      if (!isRespondSuccess(res.data)) {
        // Log the function's own wording for diagnostics, but never raise it to
        // the user: it is backend phrasing ("Invite not found") and can carry a
        // raw request-validation dump, neither of which means anything to a
        // hunter. Same split useJoinHuntByCode makes.
        console.error('respondtohuntplaninvite rejected the response', res.data?.message)
        throw new Error('The hunt invitation response was not saved.')
      }
      const result: RespondInviteResult = {
        isHuntOngoing: res.data.isHuntOngoing === true,
        huntId: res.data.huntId ?? null,
        huntState: normalizeHuntState(res.data.huntState),
      }
      // Same condition the mobile app applies: an ACCEPT of an ALREADY-RUNNING
      // hunt, never a decline and never a hunt that has yet to start — a hunt
      // weeks away must not change the user's setting.
      if (status === 'accepted' && result.isHuntOngoing && uid) {
        void enablePositionPublishing(uid)
      }
      return result
    },
    onSuccess: () => {
      // Refresh every surface that reflects the RSVP so a single response
      // updates the UI (CLUB-183):
      //  - the open hunt detail,
      //  - all of the current user's hunt lists AND counts — the broad
      //    ['clubhouse','me','hunts'] prefix covers 'upcoming', 'drafts',
      //    'ended' and 'stats'. 'stats' powers the pending-invitations
      //    count/badge, which previously stayed stale after responding,
      //  - the team's hunts list (team detail also shows RSVP state).
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', groupId, 'hunts', huntId, 'detail', uid] })
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'me', 'hunts'] })
      void qc.invalidateQueries({ queryKey: ['clubhouse', 'groups', groupId, 'hunts', uid] })
    },
  })
}
